import { Users } from 'lucide-react';
import { ResizedImage } from '@ah/design/mobile';
import {
  type Circle,
  type PlannedGathering,
} from '@ah/circles/shared';

const CATEGORY_COLOURS: Record<string, string> = {
  organization: 'bg-blue-500',
  role:         'bg-violet-500',
  industry:     'bg-amber-500',
  interest:     'bg-emerald-500',
  cause:        'bg-rose-500',
  context:      'bg-sky-500',
  goal:         'bg-orange-500',
  trait:        'bg-pink-500',
};

function GatheringRow({ gathering }: { gathering: PlannedGathering }) {
  const formatLabel: Record<string, string> = {
    online: 'Online',
    'in-person': 'In person',
    async: 'Async',
  };
  return (
    <li className="flex gap-2.5 text-[15px]">
      <div className="w-1.5 h-1.5 rounded-full bg-primary/60 mt-2 shrink-0" />
      <div className="min-w-0">
        <p className="font-medium leading-tight">{gathering.title}</p>
        <p className="text-[15px] text-muted-foreground leading-snug mt-1">{gathering.description}</p>
        <p className="text-[13px] text-muted-foreground/80 mt-1">
          {formatLabel[gathering.format] ?? gathering.format}{gathering.cadence ? ` · ${gathering.cadence}` : ''}
        </p>
      </div>
    </li>
  );
}

export function CircleCard({ circle }: { circle: Circle }) {
  const colour = CATEGORY_COLOURS[circle.category] ?? 'bg-slate-500';
  const gatherings = circle.plannedGatherings ?? [];

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
      {circle.coverUrl ? (
        <div className="w-full aspect-video bg-muted overflow-hidden">
          <ResizedImage src={circle.coverUrl} renderWidth={384} fallback={false} alt="" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className={`w-full aspect-video ${colour} flex items-center justify-center`}>
          <Users size={40} className="text-white/70" />
        </div>
      )}

      <div className="p-6 space-y-5">
        <div>
          {circle.type === 'league' ? (
            <p className="text-[11px] font-semibold uppercase tracking-wider text-primary mb-1">
              Underlings Leagues
            </p>
          ) : circle.brandName ? (
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
              {circle.brandName}
            </p>
          ) : null}
          <p className="font-bold text-xl leading-tight">
            {circle.displayTitle ?? circle.name}
          </p>
          {circle.memberCount >= 3 && (circle.memberAvatars?.length ?? 0) > 0 && (
            <div className="flex items-center gap-2 mt-3">
              <div className="flex -space-x-2">
                {circle.memberAvatars!.slice(0, 5).map((url, i) => (
                  <ResizedImage
                    key={i}
                    src={url}
                    renderWidth={28}
                    fallback={false}
                    alt=""
                    className="w-7 h-7 rounded-full object-cover ring-2 ring-card bg-muted"
                  />
                ))}
              </div>
              <p className="text-[13px] text-muted-foreground">
                {circle.memberCount} {circle.memberCount === 1 ? 'member' : 'members'}
              </p>
            </div>
          )}
        </div>

        {circle.description && (
          <p className="text-[15px] text-foreground/80 leading-relaxed">
            {circle.description}
          </p>
        )}

        {gatherings.length > 0 && (
          <div className="space-y-3">
            <p className="text-[13px] uppercase tracking-wider text-muted-foreground font-medium">Upcoming</p>
            <ul className="space-y-3">
              {gatherings.map((g, i) => (
                <GatheringRow key={i} gathering={g} />
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
